<template>
  <div class="dashboard-container">
    <el-card>
      <el-tabs v-model="activeName">
        <el-tab-pane label="SP-RR-002" name="SP-RR-002" />
      </el-tabs>
      <!-- 1. 顶部操作栏 -->
      <div class="top-bar">
        <el-date-picker
          v-model="queryDate"
          :placeholder="$t('operationCenter.SelectDate')"
          class="date-picker"
          prefix-icon="el-icon-time"
          type="date"
          value-format="yyyy-MM-dd"
        />
        <div class="btn-group">
          <el-button icon="el-icon-search" type="success" @click="handleSearch">{{ $t('userManage.search') }}
          </el-button>
          <el-button
            icon="el-icon-download" plain type="success"
            @click="handleExport"
          >{{ $t('CenterForSorting.export') }}
          </el-button>
        </div>
      </div>
      <!-- 2. 数据概览卡片区域 -->
      <div class="cards-row">
        <!-- 左侧：总览卡片 -->
        <div class="card total-card">
          <div class="card-title">{{ $t('Resource.todayTotalWorkload') }}</div>
          <div class="card-value">{{ summaryData.total }}</div>
          <div class="card-footer">
            <span class="label" style="margin-right: 20px">{{
              $t('Resource.comparedToYesterday')
            }}</span>
            <span :class="['trend', summaryData.trendArrow === '↑' ? 'up' : 'down']">
              <i :class="summaryData.trendArrow === '↑' ? 'el-icon-top' : 'el-icon-bottom'" />
              {{ summaryData.trendDiff }}
            </span>
          </div>
        </div>
        <!-- 右侧：分项卡片循环 -->
        <div v-for="(item, index) in summaryData.details" :key="index" class="card detail-card">
          <div class="card-title">{{ item.name }}</div>
          <div class="card-value">{{ item.value }}</div>
          <div class="card-footer">
            <span class="label" style="margin-right: 20px">{{ $t('trajectory.Proportion') }}</span>
            <span class="percent">{{ item.percent }}</span>
          </div>
        </div>
      </div>
    </el-card>
    <!-- 折线图区域 -->
    <div class="chart-section">
      <div class="chart-title">
        <i class="el-icon-trend-charts" style="color: #2d9a4f; margin-right: 6px" />
        <span>{{ $t('Resource.workloadTrend24h') }}</span>
      </div>
      <div ref="lineChart" style="height: 300px; width: 100%" />
    </div>

    <!-- 柱状图区域 -->
    <div class="chart-section">
      <div class="section-header">
        <i class="el-icon-s-data chart-icon" />
        <span class="title">{{ $t('Resource.warehouseEfficiencyComparison') }}</span>
      </div>
      <div ref="mainChart" class="chart-box" />
    </div>

    <!-- 底部表格区域 -->
    <div class="table-section">
      <div class="section-header">
        <i class="el-icon-s-grid table-icon" />
        <span class="title">{{ $t('Resource.hourlyWorkloadDetails') }}</span>
      </div>
      <el-table
        :data="tableData"
        :header-cell-style="{ background: '#f5f7fa', color: '#000000', fontWeight: 'bold' }"
        :summary-method="getSummaries"
        border
        show-summary
        style="width: 100%"
      >
        <el-table-column
          :label="$t('trajectory.Time')" align="center" prop="time"
          width="180"
        />
        <el-table-column
          v-for="(col, index) in columns"
          :key="index"
          :label="col.label"
          :prop="col.prop"
          align="center"
        >
          <template slot-scope="scope">
            <span v-if="scope.row[col.prop] > 0">
              {{ scope.row[col.prop] }}
              <span class="percent-text">({{ scope.row[col.prop + 'Percent'] }})</span>
            </span>
            <span v-else>0</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('Resource.totalWorkload')"
          align="center"
          prop="total"
          width="150"
        >
          <template slot-scope="scope">
            <strong>{{ scope.row.total }}</strong>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { sortingOperations, sortingOperationsExport } from '@/api/sorting/uploadRecord';
import dayjs from 'dayjs';
import { goResourceTaskList } from '@/utils/goExportList';

export default {
  name: 'WorkTrendDashboard',
  data() {
    return {
      queryDate: dayjs().format('YYYY-MM-DD'),
      // 后端原始数据
      resultData: {},
      // 卡片数据（从 resultData 计算）
      summaryData: {
        total: 0,
        trendArrow: '',
        trendDiff: 0,
        details: []
      },
      // 表格列定义
      columns: [
        { prop: 'supplyManual', label: this.$t('Resource.sortingMachineAJGRUManualFeed') },
        { prop: 'supplyAuto', label: this.$t('Resource.sortingMachineAJGRUAutoFeed') },
        { prop: 'dws', label: this.$t('Resource.sortingMachineAJGRUDWS') },
        { prop: 'pda', label: this.$t('Resource.pdaSorting') }
      ],
      // 表格数据（24行，每行一个时段）
      tableData: [],
      // ECharts 实例
      lineChartInstance: null,
      barChartInstance: null,
      activeName: 'SP-RR-002'
    };
  },
  mounted() {
    this.searchData();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.lineChartInstance) this.lineChartInstance.dispose();
    if (this.barChartInstance) this.barChartInstance.dispose();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    getSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      // 先计算每列的数值合计
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t('GlobalSetObj.sum');
          return;
        }
        const prop = column.property;
        const values = data.map((item) => Number(item[prop]));
        const sum = values.reduce((prev, curr) => prev + (isNaN(curr) ? 0 : curr), 0);
        sums[index] = sum;
      });

      // 最后一列为"合计作业量"，用它计算前几列的百分比
      const totalSum = sums[sums.length - 1] || 0;
      for (let i = 1; i < sums.length - 1; i++) {
        const percent = totalSum > 0 ? ((sums[i] / totalSum) * 100).toFixed(2) : '0.00';
        sums[i] = `${sums[i]} (${percent}%)`;
      }
      sums[sums.length - 1] = String(sums[sums.length - 1]);

      return sums;
    },
    async searchData() {
      try {
        const params = { startDate: this.queryDate || '' };
        const res = await sortingOperations(params);
        this.resultData = res.data || {};
        // 回填查询日期
        if (this.resultData.statDate) {
          this.queryDate = this.resultData.statDate;
        }
        // 更新视图
        this.buildSummaryData();
        this.buildTableData();
        this.$nextTick(() => {
          this.initLineChart();
          this.initBarChart();
        });
      } catch (error) {
        console.error('获取数据失败:', error);
      }
    },
    // 构建卡片概览数据
    buildSummaryData() {
      const d = this.resultData;
      if (!d.total) return;

      const total = d.total.dailyTotal || 0;
      const compare = d.compareData || {};
      const trendArrow = compare.arrow || '';
      const trendDiff = compare.diff || 0;

      // 分项卡片数据
      const details = [
        {
          name: this.$t('Resource.sortingMachineAJGRU'),
          value: (d.ajGru && d.ajGru.dailyTotal) || 0,
          percent: (d.ajGru && d.ajGru.percentage) || '0.00%'
        },
        {
          name: this.$t('Resource.sortingMachineAJGRUDWSAlt'),
          value: (d.dws && d.dws.dailyTotal) || 0,
          percent: (d.dws && d.dws.percentage) || '0.00%'
        },
        {
          name: this.$t('Resource.pdaSorting'),
          value: (d.pda && d.pda.dailyTotal) || 0,
          percent: (d.pda && d.pda.percentage) || '0.00%'
        }
      ];

      this.summaryData = { total, trendArrow, trendDiff, details };
    },
    // 构建表格数据（24小时明细）
    buildTableData() {
      const d = this.resultData;
      if (!d.total || !d.total.hourlyPoints) {
        this.tableData = [];
        return;
      }

      const totalPoints = d.total.hourlyPoints || [];
      const supplyManualPoints = (d.supplyManual && d.supplyManual.hourlyPoints) || [];
      const supplyAutoPoints = (d.supplyAuto && d.supplyAuto.hourlyPoints) || [];
      const dwsPoints = (d.dws && d.dws.hourlyPoints) || [];
      const pdaPoints = (d.pda && d.pda.hourlyPoints) || [];

      this.tableData = totalPoints.map((item, idx) => ({
        time: item.label || '',
        supplyManual: (supplyManualPoints[idx] && supplyManualPoints[idx].totalValue) || 0,
        supplyManualPercent:
          (supplyManualPoints[idx] && supplyManualPoints[idx].percentage) || '0.00%',
        supplyAuto: (supplyAutoPoints[idx] && supplyAutoPoints[idx].totalValue) || 0,
        supplyAutoPercent: (supplyAutoPoints[idx] && supplyAutoPoints[idx].percentage) || '0.00%',
        dws: (dwsPoints[idx] && dwsPoints[idx].totalValue) || 0,
        dwsPercent: (dwsPoints[idx] && dwsPoints[idx].percentage) || '0.00%',
        pda: (pdaPoints[idx] && pdaPoints[idx].totalValue) || 0,
        pdaPercent: (pdaPoints[idx] && pdaPoints[idx].percentage) || '0.00%',
        total: item.totalValue || 0
      }));
    },
    // 折线图：24小时作业总量趋势
    initLineChart() {
      const dom = this.$refs.lineChart;
      if (!dom) return;
      if (this.lineChartInstance) this.lineChartInstance.dispose();
      this.lineChartInstance = echarts.init(dom);

      const d = this.resultData;
      const points = (d.total && d.total.hourlyPoints) || [];
      const hours = points.map((p) => p.label || '');
      const values = points.map((p) => p.totalValue || 0);

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e0e6ed',
          borderWidth: 1,
          padding: [8, 12],
          textStyle: { color: '#333', fontSize: 12 },
          formatter: (params) => {
            const hour = params[0].name;
            const value = params[0].value;
            return `<div style="font-weight:bold;color:#2d9a4f;">${hour}</div>
                    <div>${this.$t('Resource.totalWorkloadAlt')} ${value}</div>`;
          }
        },
        grid: { left: '5%', right: '5%', bottom: '15%', top: '10%' },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: hours,
          axisLine: { lineStyle: { color: '#ccc' }},
          axisTick: { show: false },
          axisLabel: { fontSize: 12, color: '#666', interval: 1 }
        },
        yAxis: {
          type: 'value',
          min: 0,
          splitLine: { lineStyle: { color: '#eee' }},
          axisLabel: { fontSize: 12, color: '#666' }
        },
        series: [
          {
            name: this.$t('Resource.totalWorkloadAlt'),
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            itemStyle: { color: '#2d9a4f', borderColor: '#fff', borderWidth: 2 },
            lineStyle: { width: 2, color: '#2d9a4f' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(45, 154, 79, 0.1)' },
                { offset: 1, color: 'rgba(45, 154, 79, 0.02)' }
              ])
            },
            data: values
          }
        ]
      };

      this.lineChartInstance.setOption(option);
    },
    // 柱状图：仓内作业效率对比
    initBarChart() {
      const dom = this.$refs.mainChart;
      if (!dom) return;
      if (this.barChartInstance) this.barChartInstance.dispose();
      this.barChartInstance = echarts.init(dom);

      const d = this.resultData;
      const ajGruPoints = (d.ajGru && d.ajGru.hourlyPoints) || [];
      const dwsPoints = (d.dws && d.dws.hourlyPoints) || [];
      const pdaPoints = (d.pda && d.pda.hourlyPoints) || [];

      const labels = ajGruPoints.map((p) => p.label || '');
      const ajGruValues = ajGruPoints.map((p) => p.totalValue || 0);
      const dwsValues = dwsPoints.map((p) => p.totalValue || 0);
      const pdaValues = pdaPoints.map((p) => p.totalValue || 0);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        legend: {
          data: [
            this.$t('Resource.sortingMachineAJGRU'),
            this.$t('Resource.sortingMachineAJGRUDWSAlt'),
            this.$t('Resource.pdaSorting')
          ],
          top: 10,
          right: 20,
          backgroundColor: '#fff',
          padding: 10,
          borderRadius: 4
        },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#DCDFE6' }},
          axisLabel: { color: '#606266' }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { type: 'dashed' }}
        },
        series: [
          {
            name: this.$t('Resource.sortingMachineAJGRU'),
            type: 'bar',
            itemStyle: { color: '#67C23A' },
            data: ajGruValues
          },
          {
            name: this.$t('Resource.sortingMachineAJGRUDWSAlt'),
            type: 'bar',
            itemStyle: { color: '#409EFF' },
            data: dwsValues
          },
          {
            name: this.$t('Resource.pdaSorting'),
            type: 'bar',
            itemStyle: { color: '#E6A23C' },
            data: pdaValues
          }
        ]
      };

      this.barChartInstance.setOption(option);
    },
    handleResize() {
      if (this.lineChartInstance) this.lineChartInstance.resize();
      if (this.barChartInstance) this.barChartInstance.resize();
    },
    handleSearch() {
      this.searchData();
    },
    handleExport() {
      const params = { startDate: this.queryDate || '' };
      sortingOperationsExport(params).then(() => {
        goResourceTaskList();
      });
    }
  }
};
</script>

<style scoped>
.top-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.date-picker {
  width: 200px;
  margin-right: 15px;
}

.cards-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.card {
  background: #fff;
  border: 1px solid #00a651;
  border-radius: 8px;
  padding: 10px;
  box-sizing: border-box;
  flex: 1;
  min-width: 200px;
  position: relative;
}

.card-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
}

.card-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 15px;
  text-align: center;
}

.card-footer {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #909399;
}

.trend {
  font-weight: bold;
  font-size: 14px;
}

.trend.up {
  color: #67c23a;
}

.trend.down {
  color: #f56c6c;
}

.percent {
  color: #606266;
}

.chart-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.chart-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 4px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-weight: bold;
  font-size: 16px;
  color: #303133;
}

.chart-icon {
  color: #67c23a;
  font-size: 20px;
  margin-right: 8px;
}

.table-icon {
  color: #67c23a;
  font-size: 20px;
  margin-right: 8px;
}

.chart-box {
  width: 100%;
  height: 400px;
}

.percent-text {
  color: #909399;
  font-size: 12px;
}
</style>
